import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useBulkCapabilityDecision } from "@/hooks/useCapabilities";
import {
  formatCapabilityScope,
  type CapabilityBulkDecision,
} from "@/types/capabilities";
import type { CapabilityOverride } from "@/services/capabilitiesApi";

interface BulkCapabilityDecisionDialogProps {
  overrides: CapabilityOverride[];
  decision: CapabilityBulkDecision | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (succeededIds: number[]) => void;
}

const COPY: Record<
  CapabilityBulkDecision,
  { title: string; description: string; confirm: string; success: string }
> = {
  inherit: {
    title: "Delete overrides",
    description:
      "Delete these overrides. Each capability falls back to its registry default. This cannot be undone.",
    confirm: "Delete",
    success: "Deleted",
  },
  allow: {
    title: "Apply allow",
    description:
      "Delete each selected row, then write allow at the same capability and scope.",
    confirm: "Apply allow",
    success: "Applied allow",
  },
  deny: {
    title: "Apply deny",
    description:
      "Delete each selected row, then write deny at the same capability and scope.",
    confirm: "Apply deny",
    success: "Applied deny",
  },
};

export function BulkCapabilityDecisionDialog({
  overrides,
  decision,
  open,
  onOpenChange,
  onComplete,
}: BulkCapabilityDecisionDialogProps) {
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const mutation = useBulkCapabilityDecision();

  useEffect(() => {
    if (open) {
      setReason("");
      setExpiresAt("");
    }
  }, [open, decision]);

  if (!decision) return null;

  const copy = COPY[decision];
  const count = overrides.length;
  const needsReason = decision !== "inherit";

  const handleConfirm = async () => {
    if (count === 0) {
      onOpenChange(false);
      return;
    }
    if (needsReason && !reason.trim()) {
      toast.error("Add a reason for this override");
      return;
    }

    try {
      const result = await mutation.mutateAsync({
        overrides,
        decision,
        ...(needsReason ? { reason: reason.trim() } : {}),
        ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      });

      const failedCount = result.failed.length;
      const succeededCount = result.succeeded.length;
      if (failedCount === 0) {
        toast.success(
          `${copy.success} on ${succeededCount} override${succeededCount === 1 ? "" : "s"}`,
        );
      } else if (succeededCount === 0) {
        toast.error(result.failed[0]?.error ?? "Failed to apply the decision");
      } else {
        toast.message(
          `${copy.success} on ${succeededCount}; ${failedCount} failed`,
        );
      }

      onComplete?.(result.succeeded);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to apply the decision",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {copy.title}{" "}
            <span className="font-normal text-muted-foreground">
              · {count} {count === 1 ? "row" : "rows"}
            </span>
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <ul className="max-h-40 overflow-y-auto rounded-md border bg-muted/30 px-3 py-2 text-sm">
          {overrides.map((override) => (
            <li key={override.id} className="flex items-baseline gap-2 py-0.5">
              <span className="font-mono">{override.capability}</span>
              <span className="text-muted-foreground">
                {formatCapabilityScope(override.scope_type, override.scope_id)}
              </span>
              <span className="ml-auto text-xs uppercase text-muted-foreground">
                {override.state}
              </span>
            </li>
          ))}
        </ul>

        {needsReason && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="bulk-reason">
                Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="bulk-reason"
                className="mt-1"
                placeholder="Why is this override needed?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="bulk-expires">Expires (optional)</Label>
              <Input
                id="bulk-expires"
                type="datetime-local"
                className="mt-1"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={decision === "inherit" ? "destructive" : "default"}
            onClick={() => void handleConfirm()}
            disabled={mutation.isPending || count === 0}
          >
            {mutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {copy.confirm} ({count})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
